import { observable, decorate, computed } from 'mobx';
import _ from 'lodash';
import moment from 'moment';
import 'moment/locale/ru';

import { InvoicePaymentTypes, InvoiceTypes, getPaymentDaysLeft, isPaymentAlert } from './invoices';
import { authUserState } from './common';
import { checkRights, formatDate } from '../functions';

/** Класс работы с календарём платежей */
export class PaymentsState {
	constructor() {
		moment.locale('ru');

		this.CurrentMonth = formatDate("today", "YYYY-MM");
		this.Weeks = [];
		this.CurrentProvider = undefined;
		this.IsSendRequest = false;
	}

	/** Предыдущий, выбранный и следующий месяцы */
	get Months () {
		return _.map([-1, 0, 1], (offset) => {
			const month = moment(this.CurrentMonth).add(offset, "months");
			return {
				Id: formatDate(month, "YYYY-MM"),
				Name: formatDate(month, "MMMM YYYY")
			};
		});
	}

	/** Право оплаты накладных */
	get CanPay () { return checkRights("PaymentInvoices"); }

	/** Формирование полного календаря выбранного месяца */
	setMonth (dates) {
		const month = moment(this.CurrentMonth, "YYYY-MM"),
			calendarStart = month.clone().startOf("isoWeek"),
			calendarEnd = month.clone().endOf("month").endOf("isoWeek"),
			responseDays = _.keyBy(dates || [], "Date"),
			days = [];

		for (let date = calendarStart.clone(); !date.isAfter(calendarEnd, "day"); date.add(1, "day")) {
			const value = date.format("YYYY-MM-DD"),
				responseDay = responseDays[value] || {};
			days.push({
				Date: value,
				Invoices: responseDay.Invoices
			});
		}

		this.Weeks = _.map(_.chunk(days, 7), (week) => new PaymentWeek(week, this.CurrentMonth));
		this.IsSendRequest = true;
	}

	/** Выбор поставщика по дате и названию или очистка выбора */
	setProvider(date, provider) {
		if (!date || !provider) {
			this.CurrentProvider = undefined;
			return;
		}
		const day = _.find(_.flatten(_.map(this.Weeks, "Days")), { Date: date });
		this.CurrentProvider = day ? _.find(day.Providers, { Provider: provider }) : undefined;
	}
}
decorate(PaymentsState, {
	CurrentMonth: observable,
	Weeks: observable,
	CurrentProvider: observable,
	IsSendRequest: observable,
	Months: computed,
	CanPay: computed
});

/** Модель одной недели календаря платежей */
export class PaymentWeek {
	constructor(days, month) {
		this.Days = _.map(days || [], (day) => day instanceof PaymentDay ? day : new PaymentDay(day, month));
	}

	/** Диапазон дат недели */
	get Title () {
		return _.isEmpty(this.Days) ? "" : moment(_.head(this.Days).Date).format("DD.MM") + "–"
			+ moment(_.last(this.Days).Date).format("DD.MM");
	}

	/** Итоги недели по способам оплаты */
	get Amounts () {
		const invoices = _.flatMap(this.Days, (day) => _.flatMap(day.Providers, "Invoices")),
			groups = _.groupBy(invoices, "PaymentType");

		return _.compact(_.map(_.sortBy(InvoicePaymentTypes, "Priority"), (paymentType) => {
			const items = groups[paymentType.Id];
			if (_.isEmpty(items)) return null;
			const deadline = _.min(_.map(items, "DeadlinePayment")),
				status = _.min(_.map(items, "Status")),
				alert = isPaymentAlert(deadline);
			return {
				Type: paymentType.Id,
				Name: paymentType.Name,
				Icon: paymentType.Icon,
				Total: _.sumBy(_.filter(items, { Status: 2 }), "Amount"),
				Payment: _.sumBy(_.filter(items, { Status: 3 }), "Amount"),
				Alert: alert,
				Status: status,
				StatusClass: status === 3 ? "pay" : alert ? "alert" : "notPay"
			};
		}));
	}
}
decorate(PaymentWeek, {
	Days: observable,
	Title: computed,
	Amounts: computed
});

/** Модель одного дня календаря платежей */
export class PaymentDay {
	constructor(item, month) {
		item = item || {};
		this.Date = item.Date || "";
		this.Month = month || formatDate(this.Date, "YYYY-MM");
		this.Providers = _.map(_.groupBy(item.Invoices || [], "Provider"), (invoices, provider) =>
			new PaymentProvider({ Provider: provider, Date: this.Date, Invoices: invoices })
		);
	}

	/** Флаг даты за пределами выбранного месяца */
	get Disabled () { return formatDate(this.Date, "YYYY-MM") !== this.Month; }

	/** Флаг текущей даты */
	get Today () { return this.Date === formatDate("today", "YYYY-MM-DD"); }
}

/** Модель группы накладных поставщика за один день */
export class PaymentProvider {
	constructor(item) {
		item = item || {};
		this.Provider = item.Provider || "";
		this.Date = item.Date || "";
		this.Invoices = _.map(item.Invoices || [], (invoice) => {
			const type = _.find(InvoiceTypes, { Id: invoice.InvoiceType });
			return {
				...invoice,
				Name: (type ? type.ShortName : "") + " " + invoice.InvoiceNumber,
				Alert: isPaymentAlert(invoice.DeadlinePayment),
				Expired: getPaymentDaysLeft(invoice.DeadlinePayment) <= 0
			};
		});
	}

	/** Общая сумма накладных поставщика */
	get Total () { return _.sumBy(this.Invoices, "Amount"); }

	/** Минимальный срок оплаты накладных поставщика */
	get DeadlinePayment () { return _.min(_.map(this.Invoices, "DeadlinePayment")); }

	/** Минимальный статус накладных поставщика */
	get Status () { return _.min(_.map(this.Invoices, "Status")); }

	/** Приоритетный способ оплаты накладных поставщика */
	get PaymentType () {
		const paymentType = _.find(_.sortBy(InvoicePaymentTypes, "Priority"),
			(type) => _.some(this.Invoices, { PaymentType: type.Id }));
		return paymentType ? paymentType.Id : undefined;
	}

	/** Иконка приоритетного способа оплаты */
	get PaymentIcon () {
		const paymentType = _.find(InvoicePaymentTypes, { Id: this.PaymentType });
		return paymentType ? paymentType.Icon : null;
	}

	/** Флаг приближения минимального срока оплаты */
	get Alert () { return isPaymentAlert(this.DeadlinePayment); }

	/** Накладные, сгруппированные по способу оплаты */
	get PaymentGroups () {
		const groups = _.groupBy(this.Invoices, "PaymentType");

		return _.compact(_.map(_.sortBy(InvoicePaymentTypes, "Priority"), (paymentType) => {
			const invoices = groups[paymentType.Id];
			if (_.isEmpty(invoices)) return null;
			const deadline = _.min(_.map(invoices, "DeadlinePayment")),
				status = _.min(_.map(invoices, "Status")),
				alert = isPaymentAlert(deadline);
			return {
				Type: paymentType.Id,
				Name: paymentType.Name,
				Icon: paymentType.Icon,
				Total: _.sumBy(_.filter(invoices, { Status: 2 }), "Amount"),
				Payment: _.sumBy(_.filter(invoices, { Status: 3 }), "Amount"),
				TotalVAT: _.sumBy(_.filter(invoices, { Status: 2 }), "VATAmount"),
				PaymentVAT: _.sumBy(_.filter(invoices, { Status: 3 }), "VATAmount"),
				StatusClass: status === 3 ? "pay" : alert ? "alert" : "notPay",
				Invoices: _.orderBy(invoices, [
					(invoice) => invoice.Status === 3,
					"DeadlinePayment"
				], ["asc", "asc"])
			};
		}));
	}

	/** Локальное применение успешной оплаты накладной от текущего пользователя текущей датой */
	setPaid(id) {
		const invoice = _.find(this.Invoices, { Id: id });
		if (!invoice) return false;
		invoice.Status = 3;
		invoice.PaymentDate = formatDate("today", "r");
		invoice.PaymentUser = authUserState.User ? authUserState.User.Name : "";
		return true;
	}
}
decorate(PaymentProvider, {
	Invoices: observable,
	Total: computed,
	DeadlinePayment: computed,
	Status: computed,
	PaymentType: computed,
	PaymentIcon: computed,
	Alert: computed,
	PaymentGroups: computed
});
